import dayjs from 'dayjs';
import { get, postJSON, putJSON, type JSONObject } from '../../../utils/Request';
import { normalizeIdentity } from '../../../utils/identity';

export type UserStatusCode = 'NORMAL' | 'EXPIRING' | 'DISABLED';

export type UserQuery = {
  pageNum: number;
  pageSize: number;
  userCode?: string;
  displayName?: string;
  phone?: string;
  email?: string;
  status?: UserStatusCode;
  planCode?: string;
  regionCode?: string;
};

export type AdminUserRecord = {
  key: string;
  userCode: string;
  displayName: string;
  phone: string;
  email: string;
  status: string;
  planCode: string;
  planName: string;
  regionCode: string;
  registeredAt: string;
  membershipExpiresAt: string;
};

export const USER_STATUS_OPTIONS: { value: UserStatusCode; label: string }[] = [
  { value: 'NORMAL', label: '正常' },
  { value: 'EXPIRING', label: '即将到期' },
  { value: 'DISABLED', label: '已停用' }
];

export const USER_STATUS_LABEL: Record<UserStatusCode, string> = {
  NORMAL: '正常',
  EXPIRING: '即将到期',
  DISABLED: '已停用'
};

const userCodePattern = /^[0-9]{21}$/;
const phonePattern = /^\+[1-9][0-9]{7,14}$/;
const regionPattern = /^[A-Z]{2}$/;

export function validateUserQuery(input: Omit<UserQuery, 'pageNum' | 'pageSize'>): string | null {
  const userCode = input.userCode?.trim() ?? '';
  const displayName = input.displayName?.trim() ?? '';
  const phone = input.phone?.trim() ?? '';
  const email = input.email?.trim() ?? '';
  const planCode = input.planCode?.trim() ?? '';
  const regionCode = input.regionCode?.trim().toUpperCase() ?? '';
  if (userCode && !userCodePattern.test(userCode)) return '用户编码须为 21 位数字';
  if (displayName.length > 128) return '昵称最多 128 个字符';
  if (phone && !phonePattern.test(phone)) return '手机号须为 E.164 格式，例如 +8613800138000';
  if (email.length > 320) return '邮箱最多 320 个字符';
  if (planCode && !userCodePattern.test(planCode)) return '套餐编码须为 21 位数字';
  if (regionCode && !regionPattern.test(regionCode)) return '地区须为两位大写字母';
  return null;
}

export function buildUserQueryParams(query: UserQuery): JSONObject {
  const params: JSONObject = {
    pageNum: query.pageNum,
    pageSize: query.pageSize
  };
  const userCode = query.userCode?.trim();
  const displayName = query.displayName?.trim();
  const phone = query.phone?.trim();
  const email = query.email?.trim();
  const planCode = query.planCode?.trim();
  const regionCode = query.regionCode?.trim().toUpperCase();
  if (userCode) params.userCode = userCode;
  if (displayName) params.displayName = displayName;
  if (phone) params.phone = phone;
  if (email) params.email = normalizeIdentity(email);
  if (query.status) params.status = query.status;
  if (planCode) params.planCode = planCode;
  if (regionCode) params.regionCode = regionCode;
  return params;
}

export async function queryAdminUsers(query: UserQuery) {
  const res = await get({
    path: '/api/v1/users',
    params: buildUserQueryParams(query)
  });
  const page = parseUserPage(res.content ?? res.data);
  return { code: res.code, message: res.message, ...page };
}

export type QuotaDetail = {
  total: number;
  planTotal: number;
  addonTotal: number;
  used: number;
  reserved: number;
  remaining: number;
  overLimit: boolean;
};

export type MailQuotaDetail = QuotaDetail & {
  nextResetAt: string;
  unlimited: boolean;
};

export type FrozenMembership = {
  planCode: string;
  name: string;
  grade: string;
  remaining: string;
  frozenAt: string;
};

export type FeatureDescription = {
  title: string;
  displayType: string;
  supported: boolean;
  quota: string;
  unit: string;
  period: string;
  text: string;
};

export type EntitlementPackage = {
  orderNo: string;
  name: string;
  startsAt: string;
  expiresAt: string;
  features: FeatureDescription[];
};

export type AdminUserDetail = {
  userCode: string;
  displayName: string;
  phone: string;
  email: string;
  wechatOfficialAccountBound: boolean;
  wechatMiniProgramBound: boolean;
  regionCode: string;
  locale: string;
  membershipName: string;
  membershipGrade: string;
  membershipExpiresAt: string;
  shop: QuotaDetail;
  mail: MailQuotaDetail;
  status: string;
  canEnable: boolean;
  registeredAt: string;
  disabledAt: string;
  frozenMemberships: FrozenMembership[];
  entitlementPackages: EntitlementPackage[];
};

export type UserAccountStatus = 'ACTIVE' | 'DISABLED';

export type UserAccountStatusResult = {
  userCode?: string;
  status?: string;
  disabledAt?: string;
};

export type GiftOrderBody = {
  userCode: string;
  planCode: string;
  durationDays: number;
  reason: string;
  idempotencyKey: string;
};

export type GiftOrder = {
  orderNo?: string;
  orderStatus?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  amountMinor?: number;
  currency?: string;
  quoteExpiresAt?: { seconds?: number; nanos?: number } | string | null;
};

/** 赠送套餐并生成订单。同一笔赠送重试时 idempotencyKey 保持不变。返回体原样交给页面。 */
export function createGiftOrder(body: GiftOrderBody) {
  return postJSON<GiftOrder>({
    path: '/api/v1/gift-orders',
    params: body
  });
}

/** 启用或停用用户账号。reason 为操作原因。返回体原样交给页面。 */
export function updateUserAccountStatus(
  userCode: string,
  body: { status: UserAccountStatus; reason: string }
) {
  return putJSON<UserAccountStatusResult>({
    path: `/api/v1/users/${encodeURIComponent(userCode)}/status`,
    params: body
  });
}

export async function queryAdminUserDetail(userCode: string) {
  const res = await get({
    path: `/api/v1/users/${encodeURIComponent(userCode)}`
  });
  return {
    code: res.code,
    message: res.message,
    detail: parseUserDetail(res.content ?? res.data)
  };
}

function readQuota(value: unknown): QuotaDetail {
  const record = asRecord(value);
  return {
    total: record ? numberOf(record, ['total']) ?? 0 : 0,
    planTotal: record ? numberOf(record, ['planTotal']) ?? 0 : 0,
    addonTotal: record ? numberOf(record, ['addonTotal']) ?? 0 : 0,
    used: record ? numberOf(record, ['used']) ?? 0 : 0,
    reserved: record ? numberOf(record, ['reserved']) ?? 0 : 0,
    remaining: record ? numberOf(record, ['remaining']) ?? 0 : 0,
    overLimit: record?.overLimit === true
  };
}

function formatInstant(value: unknown) {
  if (typeof value === 'string' || typeof value === 'number') return formatTime(value);
  const record = asRecord(value);
  if (!record) return '';
  const seconds = numberOf(record, ['seconds']);
  if (seconds == null || seconds <= 0) return '';
  const nanos = numberOf(record, ['nanos']) ?? 0;
  return dayjs(seconds * 1000 + Math.floor(nanos / 1e6)).format('YYYY-MM-DD HH:mm');
}

function formatDuration(millis: number) {
  if (millis <= 0) return '0分钟';
  const days = Math.floor(millis / 86400000);
  const hours = Math.floor((millis % 86400000) / 3600000);
  if (days > 0) return `${days}天${hours}小时`;
  const minutes = Math.floor((millis % 3600000) / 60000);
  if (hours > 0) return `${hours}小时${minutes}分钟`;
  return `${Math.max(1, minutes)}分钟`;
}

function parseUserDetail(payload: unknown): AdminUserDetail | null {
  const root = asRecord(payload);
  if (!root) return null;
  const account = asRecord(root.account) ?? {};
  const membership = asRecord(account.membership);
  const quota = asRecord(account.quota);
  const mailRecord = asRecord(quota?.mail);
  const frozen = readList(root.frozenMemberships) ?? [];
  const packages = readList(root.entitlementPackages) ?? [];
  return {
    userCode: textOf(account, ['userCode']),
    displayName: textOf(account, ['displayName']),
    phone: textOf(account, ['phone']),
    email: textOf(account, ['email']),
    wechatOfficialAccountBound: account.wechatOfficialAccountBound === true,
    wechatMiniProgramBound: account.wechatMiniProgramBound === true,
    regionCode: textOf(account, ['regionCode']),
    locale: textOf(account, ['locale']),
    membershipName: membership ? textOf(membership, ['name']) : '',
    membershipGrade: membership ? textOf(membership, ['grade']) : '',
    membershipExpiresAt: formatInstant(membership?.expiresAt),
    shop: readQuota(quota?.shop),
    mail: {
      ...readQuota(mailRecord),
      nextResetAt: formatInstant(mailRecord?.nextResetAt),
      unlimited: mailRecord?.unlimited === true
    },
    status: textOf(root, ['status']).toUpperCase(),
    canEnable: root.canEnable === true,
    registeredAt: formatTime(root.registeredAt),
    disabledAt: formatTime(root.disabledAt),
    frozenMemberships: frozen.map((item) => ({
      planCode: textOf(item, ['planCode']),
      name: textOf(item, ['name']),
      grade: textOf(item, ['grade']),
      remaining: formatDuration(numberOf(item, ['remainingMillis']) ?? 0),
      frozenAt: formatTime(item.frozenAt)
    })),
    entitlementPackages: packages.map((item) => ({
      orderNo: textOf(item, ['orderNo']),
      name: textOf(item, ['name']),
      startsAt: formatTime(item.startsAt),
      expiresAt: formatTime(item.expiresAt),
      features: (readList(item.featureDescriptions) ?? []).map((feature) => ({
        title: textOf(feature, ['title']),
        displayType: textOf(feature, ['displayType']),
        supported: feature.supported === true,
        quota: textOf(feature, ['quota']),
        unit: textOf(feature, ['unit']),
        period: textOf(feature, ['period']),
        text: textOf(feature, ['text'])
      }))
    }))
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function readList(value: unknown): Record<string, unknown>[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
}

function textOf(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return '';
}

function numberOf(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  }
  return null;
}

function formatTime(value: unknown) {
  if (typeof value === 'number' && value > 0) {
    const ms = value < 1e12 ? value * 1000 : value;
    return dayjs(ms).format('YYYY-MM-DD HH:mm:ss');
  }
  if (typeof value === 'string' && value.trim()) {
    const text = value.trim();
    const day = dayjs(text);
    return day.isValid() ? day.format('YYYY-MM-DD HH:mm:ss') : text;
  }
  const record = asRecord(value);
  if (record) {
    const seconds = numberOf(record, ['seconds']);
    if (seconds != null && seconds > 0) {
      const nanos = numberOf(record, ['nanos']) ?? 0;
      return dayjs(seconds * 1000 + Math.floor(nanos / 1e6)).format('YYYY-MM-DD HH:mm:ss');
    }
  }
  return '';
}

function toUserRecord(record: Record<string, unknown>, index: number): AdminUserRecord {
  const userCode = textOf(record, ['userCode', 'user_code']);
  return {
    key: userCode || `user-${index}`,
    userCode,
    displayName: textOf(record, ['displayName', 'userName', 'nickname']),
    phone: textOf(record, ['phone', 'mobile']),
    email: textOf(record, ['email']),
    status: textOf(record, ['status']).toUpperCase(),
    planCode: textOf(record, ['planCode', 'plan_code']),
    planName: textOf(record, ['planName', 'planLabel', 'packageName']),
    regionCode: textOf(record, ['regionCode', 'region']),
    registeredAt: formatTime(record.registeredAt ?? record.createdAt ?? record.registerTime ?? record.createTime),
    membershipExpiresAt: formatTime(
      record.membershipExpiresAt ??
        record.expireAt ??
        record.expireDate ??
        record.expiredAt ??
        asRecord(record.membership)?.expiresAt
    )
  };
}

function parseUserPage(payload: unknown) {
  const root = asRecord(payload);
  const nested = root ? asRecord(root.page) ?? asRecord(root.data) : null;
  const list =
    readList(payload) ??
    (root
      ? readList(root.list) ?? readList(root.records) ?? readList(root.rows) ?? readList(root.items)
      : null) ??
    (nested ? readList(nested.list) ?? readList(nested.records) ?? readList(nested.rows) : null) ??
    [];
  const totalSource = nested ?? root;
  const total = totalSource ? numberOf(totalSource, ['total', 'totalCount', 'totalElements']) : null;
  return {
    list: list.map(toUserRecord),
    total: total ?? list.length
  };
}

export type UserStatus = 'normal' | 'expiring' | 'exhausted' | 'disabled';
export type UserPlan = 'pro' | 'trial' | 'deluxe';
export type RegisterChannel = 'email' | 'wechat' | 'invite';

export type AdminUserRow = {
  id: string;
  orgName: string;
  orgId: string;
  contactName: string;
  phoneMasked: string;
  email: string;
  channel: RegisterChannel;
  region: string;
  plan: UserPlan;
  usage: {
    shopUsed: number;
    shopLimit: number;
    emailUsed: number;
    emailLimit: number;
    addonShopUsed: number;
    addonShopLimit: number;
  };
  status: UserStatus;
  expireDate: string;
  registeredAt: string;
  lastActive: string;
};

export const PLAN_OPTIONS = [
  { value: 'pro', label: '专业版' },
  { value: 'trial', label: '试用会员' },
  { value: 'deluxe', label: '豪华版' }
] as const;

export const STATUS_OPTIONS = [
  { value: 'normal', label: '正常' },
  { value: 'expiring', label: '即将到期' },
  { value: 'exhausted', label: '即将耗尽' },
  { value: 'disabled', label: '已停用' }
] as const;

export const CHANNEL_OPTIONS = [
  { value: 'email', label: '邮箱' },
  { value: 'wechat', label: '微信' },
  { value: 'invite', label: '邀请码' }
] as const;

export const CHANNEL_LABEL: Record<RegisterChannel, string> = {
  email: '邮箱',
  wechat: '微信',
  invite: '邀请码'
};

export const PLAN_LABEL: Record<UserPlan, string> = {
  pro: '专业版',
  trial: '试用会员',
  deluxe: '豪华版'
};

export const STATUS_LABEL: Record<UserStatus, string> = {
  normal: '正常',
  expiring: '即将到期',
  exhausted: '即将耗尽',
  disabled: '已停用'
};

export const adminUserList: AdminUserRow[] = [
  {
    id: 'u1',
    orgName: 'VN-BeautyHub',
    orgId: 'U10021 · owner',
    contactName: '林晓',
    phoneMasked: '138****1021',
    email: 'linxiao@beautyhub.vn',
    channel: 'email',
    region: '越南',
    plan: 'pro',
    usage: {
      shopUsed: 1,
      shopLimit: 5,
      emailUsed: 1842,
      emailLimit: 3000,
      addonShopUsed: 0,
      addonShopLimit: 0
    },
    status: 'normal',
    expireDate: '2026-12-31',
    registeredAt: '2026-06-12',
    lastActive: '活跃 6天前 09:12'
  },
  {
    id: 'u2',
    orgName: 'TH-FashionLab',
    orgId: 'U10038 · owner',
    contactName: '陈可',
    phoneMasked: '159****8834',
    email: 'chenke@fashionlab.th',
    channel: 'wechat',
    region: '泰国',
    plan: 'trial',
    usage: {
      shopUsed: 2,
      shopLimit: 2,
      emailUsed: 9,
      emailLimit: 10,
      addonShopUsed: 0,
      addonShopLimit: 0
    },
    status: 'exhausted',
    expireDate: '2026-10-20',
    registeredAt: '2026-09-28',
    lastActive: '活跃 昨天 18:40'
  },
  {
    id: 'u3',
    orgName: 'ID-GadgetZone',
    orgId: 'U10055 · owner',
    contactName: '王磊',
    phoneMasked: '186****2290',
    email: 'wanglei@gadget.id',
    channel: 'email',
    region: '印尼',
    plan: 'deluxe',
    usage: {
      shopUsed: 8,
      shopLimit: 10,
      emailUsed: 6420,
      emailLimit: 10000,
      addonShopUsed: 2,
      addonShopLimit: 3
    },
    status: 'normal',
    expireDate: '2027-03-15',
    registeredAt: '2026-03-01',
    lastActive: '活跃 2小时前'
  },
  {
    id: 'u4',
    orgName: 'MY-HomeStyle',
    orgId: 'U10072 · owner',
    contactName: '赵敏',
    phoneMasked: '137****6612',
    email: 'zhaomin@homestyle.my',
    channel: 'invite',
    region: '马来',
    plan: 'pro',
    usage: {
      shopUsed: 5,
      shopLimit: 5,
      emailUsed: 2980,
      emailLimit: 3000,
      addonShopUsed: 610,
      addonShopLimit: 500
    },
    status: 'expiring',
    expireDate: '2026-10-18',
    registeredAt: '2026-01-20',
    lastActive: '活跃 3天前 11:05'
  },
  {
    id: 'u5',
    orgName: 'PH-GlowShop',
    orgId: 'U10089 · owner',
    contactName: '周晴',
    phoneMasked: '151****4477',
    email: 'zhouqing@glow.ph',
    channel: 'email',
    region: '菲律宾',
    plan: 'trial',
    usage: {
      shopUsed: 0,
      shopLimit: 2,
      emailUsed: 0,
      emailLimit: 10,
      addonShopUsed: 0,
      addonShopLimit: 0
    },
    status: 'disabled',
    expireDate: '2026-09-01',
    registeredAt: '2026-08-10',
    lastActive: '活跃 32天前'
  },
  {
    id: 'u6',
    orgName: 'SG-TechMart',
    orgId: 'U10102 · owner',
    contactName: '韩雪',
    phoneMasked: '188****9901',
    email: 'hanxue@techmart.sg',
    channel: 'wechat',
    region: '新加坡',
    plan: 'deluxe',
    usage: {
      shopUsed: 3,
      shopLimit: 10,
      emailUsed: 2100,
      emailLimit: 10000,
      addonShopUsed: 1,
      addonShopLimit: 5
    },
    status: 'normal',
    expireDate: '2027-01-08',
    registeredAt: '2026-05-16',
    lastActive: '活跃 今天 08:22'
  }
];
